import React, { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Card from './Card';

const COLUMN_COUNT_DESKTOP = 4;
const COLUMN_COUNT_MOBILE = 1;
const CARD_GAP = 24;
const SCROLL_SPEED = 0.4; // px per frame — slow and ambient
const COPIES = 8; // repeat posts enough times to always fill the screen

function useColumns() {
  const [cols, setCols] = useState(
    window.innerWidth < 640 ? COLUMN_COUNT_MOBILE : COLUMN_COUNT_DESKTOP
  );
  useEffect(() => {
    const handler = () =>
      setCols(window.innerWidth < 640 ? COLUMN_COUNT_MOBILE : COLUMN_COUNT_DESKTOP);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return cols;
}

function Column({ posts, theme, columnIndex, totalCols }) {
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const touchStartRef = useRef(0);

  const getSingleHeight = useCallback(() => {
    return (trackRef.current?.scrollHeight / COPIES) || 1;
  }, []);

  const wrapPos = useCallback((pos) => {
    const h = getSingleHeight();
    if (pos < 0) return pos + h;
    if (pos >= h) return pos - h;
    return pos;
  }, [getSingleHeight]);

  const animate = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const singleHeight = getSingleHeight();
    if (singleHeight === 0) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }

    if (!pausedRef.current) {
      posRef.current = wrapPos(posRef.current + SCROLL_SPEED);
      track.style.transform = `translateY(-${posRef.current}px)`;
    }

    rafRef.current = requestAnimationFrame(animate);
  }, [getSingleHeight, wrapPos]);

  // Attach wheel listener as non-passive so we can preventDefault
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onWheel = (e) => {
      e.preventDefault();
      const track = trackRef.current;
      if (!track) return;
      posRef.current = wrapPos(posRef.current + e.deltaY * 0.6);
      track.style.transform = `translateY(-${posRef.current}px)`;
    };

    const onTouchStart = (e) => {
      touchStartRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      const track = trackRef.current;
      if (!track) return;
      const delta = touchStartRef.current - e.touches[0].clientY;
      touchStartRef.current = e.touches[0].clientY;
      posRef.current = wrapPos(posRef.current + delta);
      track.style.transform = `translateY(-${posRef.current}px)`;
    };

    wrapper.addEventListener('wheel', onWheel, { passive: false });
    wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      wrapper.removeEventListener('wheel', onWheel);
      wrapper.removeEventListener('touchstart', onTouchStart);
      wrapper.removeEventListener('touchmove', onTouchMove);
    };
  }, [wrapPos]);

  useEffect(() => {
    // Wait one frame so the DOM has painted and scrollHeight is accurate
    const raf = requestAnimationFrame(() => {
      posRef.current = (columnIndex / totalCols) * getSingleHeight();
      rafRef.current = requestAnimationFrame(animate);
    });
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animate, columnIndex, totalCols, getSingleHeight]);

  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeave = () => { pausedRef.current = false; };

  const repeated = Array.from({ length: COPIES }, () => posts).flat();

  const styles = {
    wrapper: {
      flex: 1,
      overflow: 'hidden',
      height: '100%',
      position: 'relative',
      cursor: 'grab',
    },
    track: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${CARD_GAP}px`,
      paddingBottom: `${CARD_GAP}px`,
      willChange: 'transform',
    },
  };

  return (
    <div
      ref={wrapperRef}
      style={styles.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div ref={trackRef} style={styles.track}>
        {repeated.map((post, i) => (
          <Card key={`${post.id}-${i}`} post={post} theme={theme} />
        ))}
      </div>
    </div>
  );
}

export default function Wall({ refreshTrigger, theme }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const cols = useColumns();

  const fetchPosts = useCallback(async () => {
    const { data } = await supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setPosts(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts, refreshTrigger]);

  useEffect(() => {
    const channel = supabase
      .channel('posts-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'posts' }, (payload) => {
        setPosts(prev => [payload.new, ...prev]);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // Distribute posts across columns in round-robin order
  const columns = Array.from({ length: cols }, () => posts);

  const isDark = theme === 'dark';

  const styles = {
    wall: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      gap: `${CARD_GAP}px`,
      padding: `${CARD_GAP}px`,
      overflow: 'hidden',
      background: isDark ? '#000' : '#fff',
    },
    empty: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
      fontSize: '1.1rem',
      letterSpacing: '0.05em',
      pointerEvents: 'none',
    },
  };

  if (loading) return <div style={styles.wall} />;

  if (posts.length === 0) {
    return (
      <div style={styles.wall}>
        <div style={styles.empty}>Be the first to leave a memory.</div>
      </div>
    );
  }

  return (
    <div style={styles.wall}>
      {columns.map((colPosts, i) => (
        <Column
          key={i}
          columnIndex={i}
          totalCols={columns.length}
          posts={colPosts}
          theme={theme}
        />
      ))}
    </div>
  );
}
